// @ts-strict-ignore
import { Button } from "@dashboard/components/Button";
import {
  ConfirmButton,
  ConfirmButtonTransitionState,
} from "@dashboard/components/ConfirmButton";
import { ExportErrorFragment } from "@dashboard/graphql";
import useForm from "@dashboard/hooks/useForm";
import useModalDialogErrors from "@dashboard/hooks/useModalDialogErrors";
import useModalDialogOpen from "@dashboard/hooks/useModalDialogOpen";
import { buttonMessages } from "@dashboard/intl";
import ExportDialogSettings, {
  ExportItemsQuantity,
} from "@dashboard/products/components/ProductExportDialog/ExportDialogSettings";
import {
  ExportSettingsFormData,
  exportSettingsInitialFormData,
} from "@dashboard/products/components/ProductExportDialog/types";
import { DialogProps } from "@dashboard/types";
import getExportErrorMessage from "@dashboard/utils/errors/export";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import React from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { orderExportDialogMessages as messages } from "./messages";

export interface OrderExportDialogProps extends DialogProps {
  confirmButtonState: ConfirmButtonTransitionState;
  errors: ExportErrorFragment[];
  ordersQuantity: ExportItemsQuantity;
  selectedOrders: number;
  onSubmit: (data: ExportSettingsFormData) => void;
}

const OrderExportDialog: React.FC<OrderExportDialogProps> = ({
  confirmButtonState,
  errors,
  ordersQuantity,
  selectedOrders,
  onClose,
  onSubmit,
  open,
}) => {
  const intl = useIntl();
  const dialogErrors = useModalDialogErrors(errors, open);
  const notFormErrors = dialogErrors.filter(err => !err.field);
  const { change, data, reset, submit } = useForm(
    exportSettingsInitialFormData,
    onSubmit,
  );

  useModalDialogOpen(open, {
    onClose: () => reset(),
  });

  const exportScopeLabels = {
    allItems: intl.formatMessage(messages.allItemsLabel, {
      number: ordersQuantity.all || "...",
    }),
    selectedItems: intl.formatMessage(messages.selectedItemsLabel, {
      number: selectedOrders,
    }),
  };

  return (
    <Dialog onClose={onClose} open={open} maxWidth="sm" fullWidth>
      <DialogTitle disableTypography>
        <FormattedMessage {...messages.title} />
      </DialogTitle>
      <DialogContent>
        <ExportDialogSettings
          data={data}
          errors={dialogErrors}
          onChange={change}
          itemsQuantity={ordersQuantity}
          selectedItems={selectedOrders}
          exportScopeLabels={exportScopeLabels}
        />
      </DialogContent>

      {notFormErrors.length > 0 && (
        <DialogContent>
          {notFormErrors.map(err => (
            <Typography color="error" key={err.field + err.code}>
              {getExportErrorMessage(err, intl)}
            </Typography>
          ))}
        </DialogContent>
      )}

      <DialogActions>
        <Button
          variant="secondary"
          color="text"
          onClick={onClose}
          data-test-id="cancel"
        >
          <FormattedMessage {...buttonMessages.cancel} />
        </Button>
        <ConfirmButton
          transitionState={confirmButtonState}
          type="submit"
          data-test-id="submit"
          onClick={submit}
        >
          <FormattedMessage {...messages.confirmButtonLabel} />
        </ConfirmButton>
      </DialogActions>
    </Dialog>
  );
};

OrderExportDialog.displayName = "OrderExportDialog";
export default OrderExportDialog;
